import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { Button, Checkbox, Input, NumberInput, Select } from "../ui";
import { useSettings } from "../app/settings";
import { useCoreDoc } from "../app/useCoreDoc";
import { AreaMap } from "./AreaMap";

type Falloff =
  | { kind: "step"; radius_km: number }
  | { kind: "linear"; zero_km: number }
  | { kind: "exponential"; half_km: number; cutoff_km: number }
  | { kind: "gaussian"; sigma_km: number; cutoff_km: number }
  | { kind: "piecewise"; points: [number, number][] };

interface Area {
  id: number;
  name: string;
  lat: number;
  lon: number;
  weight: number;
  falloff: Falloff;
  enabled: boolean;
  reachKm: number;
}

/** Default parameters when the kind changes, scaled to how far the area reaches now (as Joblupe does). */
function switchKind(kind: Falloff["kind"], reach: number): Falloff {
  const r = Math.max(1, Math.round(reach));
  switch (kind) {
    case "step":
      return { kind, radius_km: r };
    case "linear":
      return { kind, zero_km: r };
    case "exponential":
      return { kind, half_km: Math.max(1, Math.round(r / 4)), cutoff_km: r };
    case "gaussian":
      return { kind, sigma_km: Math.max(1, Math.round(r / 2.5)), cutoff_km: r };
    case "piecewise":
      return {
        kind,
        points: [
          [0, 1],
          [Math.max(1, Math.round(r / 2)), 0.5],
          [r, 0],
        ],
      };
  }
}

async function call(method: string, path: string, body?: unknown) {
  const res = await fetch(`/api/internal/places${path}`, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? {} : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json()) as { areas?: Area[]; reachM?: number; error?: string; places?: Hit[] };
  if (!res.ok) throw new Error(data.error ?? "error.generic");
  return data;
}

interface Hit {
  name: string;
  lat: number;
  lon: number;
}

/**
 * Settings → Signals → Search areas (ADR 0025): where *my* meta places are looked up, in every workspace. An area
 * is a centre (found by name or clicked on the map), a weight and a falloff whose cutoff is how far it reaches;
 * a place found there scores weight × falloff, and the best 200 are kept.
 */
export function SearchAreas() {
  const { t } = useTranslation();
  const { handle } = useCoreDoc();
  const reduced = useSettings(handle).a11y.reducedMotion;
  const [areas, setAreas] = useState<Area[]>([]);
  const [reach, setReach] = useState(150);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<Hit[]>([]);
  const [error, setError] = useState<string | null>(null);

  const apply = useCallback((d: { areas?: Area[]; reachM?: number }) => {
    if (d.areas) setAreas(d.areas);
    if (typeof d.reachM === "number") setReach(d.reachM);
  }, []);
  const run = useCallback(
    (p: Promise<{ areas?: Area[]; reachM?: number }>) => {
      setError(null);
      p.then(apply, (e: Error) => setError(e.message));
    },
    [apply],
  );
  useEffect(() => {
    call("GET", "/areas").then(apply, (e: Error) => setError(e.message));
  }, [apply]);
  const searching = q.trim().length >= 2;
  useEffect(() => {
    if (!searching) return;
    const timer = setTimeout(() => {
      call("GET", `/search?q=${encodeURIComponent(q.trim())}`).then(
        (d) => setHits(d.places ?? []),
        () => setHits([]),
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [q, searching]);
  const shownHits = searching ? hits : [];

  const add = (name: string, lat: number, lon: number) => {
    setQ("");
    setHits([]);
    run(call("POST", "/areas", { name, lat, lon, weight: 1, falloff: { kind: "step", radius_km: 5 } }));
  };
  const patch = (a: Area, body: Partial<Area>) => run(call("PATCH", `/areas/${a.id}`, body));

  return (
    <div className="flex flex-col gap-3" data-testid="search-areas">
      <h3 className="text-[13px] font-semibold">{t("metaPlaces.title")}</h3>
      <p className="text-[12.5px] text-neutral-500">{t("metaPlaces.hint")}</p>
      <label className="flex items-center gap-2 text-[13px]">
        <span className="text-neutral-500">{t("metaPlaces.reach")}</span>
        <NumberInput
          min={25}
          max={2000}
          value={reach}
          className="kss-control kss-field h-8 w-20 px-2 text-sm"
          onCommit={(n) => run(call("PUT", "/areas", { reachM: n }))}
          aria-label={t("metaPlaces.reach")}
          data-testid="search-reach"
        />
        <span className="text-neutral-500">m</span>
      </label>
      <div className="relative">
        <Input
          compact
          value={q}
          placeholder={t("metaPlaces.findPlaceholder")}
          aria-label={t("metaPlaces.find")}
          onChange={(e) => setQ(e.target.value)}
          data-testid="search-area-find"
        />
        {shownHits.length > 0 && (
          <ul
            role="listbox"
            className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border border-neutral-200 bg-[var(--surface)] p-1 text-sm shadow-[var(--shadow-md)] dark:border-neutral-700"
          >
            {shownHits.map((h) => (
              <li key={`${h.lat},${h.lon}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="w-full rounded-sm px-2 py-1 text-left hover:bg-neutral-100 dark:hover:bg-neutral-800"
                  onClick={() => add(h.name, h.lat, h.lon)}
                  data-testid="search-area-hit"
                >
                  {h.name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <AreaMap
        areas={areas}
        reducedMotion={reduced}
        onAdd={(lat, lon) => add(t("metaPlaces.newArea", { n: areas.length + 1 }), lat, lon)}
      />
      <p className="text-[12px] text-neutral-500">{t("metaPlaces.mapHint")}</p>
      {areas.length === 0 && <p className="text-[12.5px] text-neutral-500">{t("metaPlaces.none")}</p>}
      <ul className="flex flex-col gap-2">
        {areas.map((a) => (
          <li
            key={a.id}
            className="flex flex-col gap-2 rounded-md border border-neutral-200 p-2 dark:border-neutral-800"
            data-testid="search-area"
          >
            <div className="flex flex-wrap items-center gap-2">
              <Checkbox
                checked={a.enabled}
                label={t("metaPlaces.enabled", { name: a.name })}
                onChange={(v) => patch(a, { enabled: v })}
              />
              <Input
                compact
                defaultValue={a.name}
                key={`${a.id}:${a.name}`}
                aria-label={t("metaPlaces.name")}
                className="w-44"
                onBlur={(e) =>
                  e.target.value.trim() && e.target.value !== a.name && patch(a, { name: e.target.value })
                }
              />
              <span className="text-[12px] text-neutral-500">
                {t("metaPlaces.reachesKm", { km: Math.round(a.reachKm * 10) / 10 })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                aria-label={t("metaPlaces.remove", { name: a.name })}
                onClick={() => run(call("DELETE", `/areas/${a.id}`))}
              >
                <Trash2 size={14} aria-hidden />
              </Button>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-[12.5px]">
              <label className="flex items-center gap-1.5">
                <span className="text-neutral-500">{t("metaPlaces.weight")}</span>
                <Input
                  compact
                  type="number"
                  min={0}
                  max={10}
                  step={0.1}
                  defaultValue={a.weight}
                  key={`${a.id}:w:${a.weight}`}
                  className="w-16"
                  onBlur={(e) => {
                    const w = Number(e.target.value);
                    if (Number.isFinite(w) && w >= 0 && w <= 10 && w !== a.weight) patch(a, { weight: w });
                  }}
                />
              </label>
              <label className="flex items-center gap-1.5">
                <span className="text-neutral-500">{t("metaPlaces.falloff")}</span>
                <Select
                  compact
                  value={a.falloff.kind}
                  onChange={(e) =>
                    patch(a, { falloff: switchKind(e.target.value as Falloff["kind"], a.reachKm) })
                  }
                  data-testid="search-area-kind"
                >
                  {(["step", "linear", "exponential", "gaussian", "piecewise"] as const).map((k) => (
                    <option key={k} value={k}>
                      {t(`metaPlaces.kind.${k}`)}
                    </option>
                  ))}
                </Select>
              </label>
              <FalloffFields f={a.falloff} onChange={(f) => patch(a, { falloff: f })} />
            </div>
          </li>
        ))}
      </ul>
      {error && (
        <p role="alert" className="text-[12.5px] text-err">
          {t(`errors.${error}`, { defaultValue: t("error.generic") })}
        </p>
      )}
    </div>
  );
}

/** The kilometres of one falloff kind; piecewise as "km:value" pairs. */
function FalloffFields({ f, onChange }: { f: Falloff; onChange(f: Falloff): void }) {
  const { t } = useTranslation();
  const km = (label: string, value: number, set: (n: number) => Falloff) => (
    <label className="flex items-center gap-1.5" key={label}>
      <span className="text-neutral-500">{t(`metaPlaces.param.${label}`)}</span>
      <NumberInput
        min={1}
        max={500}
        value={Math.round(value)}
        className="kss-control kss-field h-[30px] w-16 px-2 text-[13px]"
        onCommit={(n) => onChange(set(n))}
        aria-label={t(`metaPlaces.param.${label}`)}
      />
      <span className="text-neutral-500">km</span>
    </label>
  );
  switch (f.kind) {
    case "step":
      return km("radius", f.radius_km, (n) => ({ ...f, radius_km: n }));
    case "linear":
      return km("zeroAt", f.zero_km, (n) => ({ ...f, zero_km: n }));
    case "exponential":
      return (
        <>
          {km("half", f.half_km, (n) => ({ ...f, half_km: n }))}
          {km("cutoff", f.cutoff_km, (n) => ({ ...f, cutoff_km: n }))}
        </>
      );
    case "gaussian":
      return (
        <>
          {km("sigma", f.sigma_km, (n) => ({ ...f, sigma_km: n }))}
          {km("cutoff", f.cutoff_km, (n) => ({ ...f, cutoff_km: n }))}
        </>
      );
    case "piecewise":
      return (
        <label className="flex items-center gap-1.5">
          <span className="text-neutral-500">{t("metaPlaces.param.points")}</span>
          <Input
            compact
            className="w-56"
            defaultValue={f.points.map(([k, v]) => `${k}:${v}`).join(" ")}
            key={f.points.map((p) => p.join(":")).join(" ")}
            onBlur={(e) => {
              const points = e.target.value
                .trim()
                .split(/\s+/)
                .map((p) => p.split(":").map(Number) as [number, number]);
              if (points.length >= 2 && points.every((p) => p.length === 2 && p.every(Number.isFinite)))
                onChange({ kind: "piecewise", points });
            }}
          />
        </label>
      );
  }
}
